import { Trash2, CalendarOff, GraduationCap, Check, AlertCircle } from 'lucide-react';
import { dateKey, MONTHS_FR } from '../../utils/dateUtils';
import type { GridRow, Ventilation } from './types';

interface HoursGridProps {
  rows: GridRow[];
  weekDays: Date[];
  isLocked: boolean;
  isBE: boolean;
  ventilations: Record<string, Ventilation>;
  isOutsideClosureMonth: (day: Date) => boolean;
  isLockedForDay: (day: Date) => boolean;
  isDayBlocked: (day: Date) => boolean;
  isTraining: (day: Date) => boolean;
  onHoursChange: (rowIdx: number, dateStr: string, value: string) => void;
  onRemoveProject: (idx: number) => void;
  onOpenVentilation: (projectId: string, dateStr: string, totalHours: number) => void;
}

export function HoursGrid({
  rows,
  weekDays,
  isLocked,
  isBE,
  ventilations,
  isOutsideClosureMonth,
  isLockedForDay,
  isDayBlocked,
  isTraining,
  onHoursChange,
  onRemoveProject,
  onOpenVentilation,
}: HoursGridProps) {
  return (
    <div style={{ background: 'white', borderRadius: '20px', border: '1px solid #e2e8f0', overflow: 'hidden', marginBottom: '40px', boxShadow:'0 2px 4px rgba(0,0,0,0.05)' }}>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', minWidth: '900px', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
          <colgroup>
            <col style={{ width: '220px' }} />
            {weekDays.map((_, i) => <col key={`col-main-${i}`} style={{ width: '97px' }} />)}
          </colgroup>
          <thead>
            <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
              <th style={{ padding: '16px 8px', textAlign: 'left', color: '#64748b', fontSize:'0.9rem' }}>CHANTIER / PROJET</th>
              {weekDays.map(day => {
                const dStr = dateKey(day);
                const isForm = isTraining(day);
                const isOutsideMonth = isOutsideClosureMonth(day);
                return (
                  <th key={dStr} style={{
                    padding: '16px 8px',
                    textAlign: 'center',
                    background: isOutsideMonth ? '#e2e8f0' : (isForm ? '#f1f5f9' : 'transparent'),
                    color: isOutsideMonth ? '#94a3b8' : ((day.getDay()===0||day.getDay()===6 || isDayBlocked(day)) ? '#ef4444' : (isForm ? '#4338ca' : '#475569')),
                    opacity: isOutsideMonth ? 0.7 : 1
                  }}>
                    <div style={{ fontSize: '0.85rem', textTransform: 'uppercase', fontWeight:'600' }}>{day.toLocaleDateString('fr-FR', { weekday: 'short' })}</div>
                    <div style={{ fontSize: '1.4rem', fontWeight: '800', marginTop:'4px' }}>{day.getDate()}</div>
                    {isForm && !isOutsideMonth && <div style={{ fontSize:'0.75rem', marginTop:'4px', fontWeight:'700', color:'#4338ca' }}>FORM.</div>}
                    {isOutsideMonth && <div style={{ fontSize:'0.65rem', marginTop:'4px', fontWeight:'600', color:'#94a3b8' }}>{MONTHS_FR[day.getMonth()].substring(0, 3).toUpperCase()}</div>}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={8} style={{ padding: '80px', textAlign: 'center', color: '#94a3b8', fontStyle: 'italic', fontSize:'1.1rem' }}>Aucune activité saisie pour cette semaine.</td></tr>
            ) : (
              rows.map((row, idx) => (
                <tr key={row.projectId} style={{ borderBottom: '1px solid #f1f5f9' }}>
                  <td style={{ padding: '16px', fontWeight: '600', color: '#334155', fontSize:'1.1rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                      <span style={{ flex: 1 }}>{row.projectName}</span>
                      {!isLocked && (
                        <button
                          onClick={() => onRemoveProject(idx)}
                          title="Supprimer ce chantier"
                          style={{
                            background: 'transparent',
                            border: 'none',
                            color: '#94a3b8',
                            cursor: 'pointer',
                            padding: '4px',
                            borderRadius: '6px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'all 0.2s'
                          }}
                          onMouseEnter={e => {
                            e.currentTarget.style.background = '#fee2e2';
                            e.currentTarget.style.color = '#ef4444';
                          }}
                          onMouseLeave={e => {
                            e.currentTarget.style.background = 'transparent';
                            e.currentTarget.style.color = '#94a3b8';
                          }}
                        >
                          <Trash2 size={16} />
                        </button>
                      )}
                    </div>
                  </td>
                  {weekDays.map(day => {
                    const dKey = dateKey(day);
                    const isForm = isTraining(day);
                    const isWeekend = day.getDay() === 0 || day.getDay() === 6;
                    const isBlocked = isDayBlocked(day);
                    const isOutsideMonth = isOutsideClosureMonth(day);
                    const hours = row.hours[dKey] || 0;
                    // NOUVEAU : Verrouillage jour par jour (pour semaines à cheval)
                    const isDayLocked = isLockedForDay(day);

                    // VENTILATION BE
                    const ventKey = `${row.projectId}_${dKey}`;
                    const vent = ventilations[ventKey];
                    const totalVent = vent ? (vent.etude + vent.suivi + vent.mise_en_service + vent.chantier) : 0;
                    const isVentilated = isBE && hours > 0 && Math.abs(totalVent - hours) < 0.01;
                    const needsVentilation = isBE && hours > 0 && !isVentilated;

                    return (
                      <td key={dKey} style={{ padding: '10px', background: isOutsideMonth ? '#f1f5f9' : (isForm ? '#e0e7ff' : (isBlocked ? '#e5e7eb' : (isWeekend ? '#fffdf2' : 'white'))), position: 'relative', borderLeft:'1px solid #f8fafc' }}>
                        {isOutsideMonth ? (
                          // CELLULE HORS MOIS DE SAISIE : grisée, non modifiable
                          <div style={{ display:'flex', flexDirection:'column', justifyContent:'center', alignItems:'center', color:'#94a3b8', height:'54px' }} title={`Ce jour sera à saisir sur la feuille de ${MONTHS_FR[day.getMonth()]}`}>
                            <CalendarOff size={20} style={{ opacity: 0.5 }} />
                            <span style={{ fontSize:'0.7rem', marginTop:'4px' }}>{MONTHS_FR[day.getMonth()].substring(0, 3)}.</span>
                          </div>
                        ) : isForm ? (
                            // CELLULE FORMATION : 7h FIXE, VISUEL SPÉCIFIQUE
                            <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', height:'54px', color:'#4338ca', fontWeight:'700', opacity: 0.8 }}>
                                <GraduationCap size={24} />
                                <span style={{ fontSize:'0.9rem', marginTop:'4px' }}>7h</span>
                            </div>
                        ) : isBlocked ? (
                          <div style={{ display:'flex', justifyContent:'center', color:'#9ca3af', padding:'10px' }} title="Jour non travaillé (Férié ou Congé)">
                            <CalendarOff size={28} />
                          </div>
                        ) : (
                          <>
                            <input
                              type="number" disabled={isDayLocked} placeholder="-" min="0" max="24" step="0.01"
                              value={row.hours[dKey] || ''}
                              onChange={(e) => onHoursChange(idx, dKey, e.target.value)}
                              style={{
                                width: '100%', height:'54px', textAlign: 'center', fontSize: '1.3rem',
                                border: needsVentilation ? '2px solid #ef4444' : '1px solid #e2e8f0', borderRadius: '12px',
                                backgroundColor: isDayLocked ? '#f8fafc' : 'white',
                                color: isDayLocked ? '#94a3b8' : '#1e293b',
                                fontWeight: hours > 0 ? '700' : '400'
                              }}
                            />

                            {/* BOUTON VENTILATION (Si BE) */}
                            {hours > 0 && !isDayLocked && isBE && (
                              <div style={{
                                position: 'absolute', bottom: '10px', right: '10px',
                                display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'flex-end'
                              }}>
                                <button
                                  onClick={() => onOpenVentilation(row.projectId, dKey, hours)}
                                  style={{
                                    width: '26px', height: '26px', borderRadius: '50%', border: 'none',
                                    background: isVentilated ? '#10b981' : '#ef4444', color: 'white',
                                    cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    boxShadow:'0 2px 4px rgba(0,0,0,0.2)'
                                  }}
                                  title={isVentilated ? "Ventilation OK" : "Ventiler les heures"}
                                >
                                  {isVentilated ? <Check size={14} /> : <AlertCircle size={14} />}
                                </button>
                              </div>
                            )}
                          </>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
